import {
  BriefcaseOutlineIcon,
  ChevronDownOutlineIcon,
  DocumentTextOutlineIcon,
  Icon,
} from '@fountain/ripple/icon';
import {
  Button,
  CircularProgress,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ApprovalsService } from 'api-clients/monolith';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { BackToCompanySettings } from 'components/BackToCompanySettings';
import GlobalNav from 'components/GlobalNav';
import SearchInput from 'components/SearchInput';
import {
  makeSelectApprovalDynamicApproversEnabled,
  makeSelectPolicies,
} from 'containers/Auth_old/selectors';
import { useApiService } from 'hooks/useApiService';
import { useDebounce } from 'hooks/useDebounce';

import { mayManageApprovalRules, mayReachApprovals } from './access';
import { ApprovalsQueue } from './ApprovalsQueue';
import queueMessages from './ApprovalsQueue/messages';
import {
  ApprovalsTab,
  ApprovalsTabs,
  tabId,
  tabPanelId,
} from './ApprovalsTabs';
import { CatalogResponse, CatalogState } from './conditionForm';
import { DeleteRuleDialog } from './DeleteRuleDialog';
import messages from './messages';
import { RuleEditorPanel } from './RuleEditorPanel';
import { RuleRow } from './RuleRow';
import { useStyles } from './styles';
import { SubjectTypeToggle } from './SubjectTypeToggle';
import { ApprovalRule, OFFER, OPENING, SubjectType, UserOption } from './types';
import { useApprovalRules } from './useApprovalRules';
import { useApprovalsTab } from './useApprovalsTab';
import { useSubjectTypeView } from './useSubjectTypeView';

const SEARCH_DEBOUNCE_MS = 300;

// Static ids rather than `useMemo`'d unique ones: this page renders one create
// menu, and the pair only has to be stable enough for the button's
// `aria-controls` and the menu's `aria-labelledby` to point at each other.
//
// Only one of the two survives a close. `aria-labelledby` lives on the menu, so
// it goes when the menu does; `aria-controls` lives on the button, which stays,
// and MUI unmounts the popover rather than hiding it (`keepMounted` is off), so
// a static `aria-controls` would name an element that no longer exists. It is
// set only while the menu is open — the same shape `MoreNavMenu` uses.
const CREATE_BUTTON_ID = 'approval-rules-create';
const CREATE_MENU_ID = 'approval-rules-create-menu';

export const APPROVER_RULES_TAB = 'approver-rules';
export const APPROVALS_TAB = 'approvals';

/**
 * Rule, Type, Trigger, Approvers, Enabled, and the actions cell — so a state
 * rendered in the table body spans the full width rather than sitting under the
 * first column.
 */
const COLUMN_COUNT = 6;

/**
 * The page's tab registry, in display order, and the plug point the rest of the
 * approvals surface lands on.
 *
 * The design has three — Approver rules, Approvals, Auto-approval. Two exist:
 * the rules editor and the approvals queue. Auto-approval adds the third entry
 * when it ships.
 *
 * `ApprovalsTabs` renders nothing below two entries, so the strip is visible
 * because of the second.
 */
const TABS: ApprovalsTab[] = [
  { id: APPROVER_RULES_TAB, label: messages.approverRulesTab },
  { id: APPROVALS_TAB, label: queueMessages.approvalsTab },
];

/**
 * The approvals surface (AXHE-3945, shell in AXHE-4472).
 *
 * Deliberately a new surface rather than a tab on the legacy `OpeningApproval`
 * page. That page is openings-only and backed by the legacy approver-group
 * tables; this one is entity-agnostic and backed by `Approvals::Rule`. Keeping
 * them apart means the old page stays untouched and running during cutover, so
 * the two can be compared side by side — and rules the legacy backfill still
 * owns are shown here read-only rather than being editable in two places at
 * once (see `managed_by_legacy_group`).
 */
export const ApprovalRules: React.FC = () => {
  const styles = useStyles();
  const intl = useIntl();

  // The same predicate the route mounts on. Passed to `BackToCompanySettings`,
  // which redirects on `manage_account` by default and would otherwise send
  // away every non-admin this page was opened for.
  const policies = useSelector(makeSelectPolicies());
  const dynamicApproversEnabled = useSelector(
    makeSelectApprovalDynamicApproversEnabled(),
  );
  const mayManageRules = mayManageApprovalRules(policies);
  const mayViewApprovals = mayReachApprovals(policies);

  // The rules half of the page, and the three requests behind it, belong to
  // `manage_approval_rules` alone. `RulesController` and
  // `FieldCatalogController` both authorize `AccountPolicy#manage_approval_rules?`,
  // so an approver admitted by `approve_offers` or `approve_openings` would
  // otherwise land on the default tab and watch two 403s render as a failed
  // rules table, under controls that create and delete the gates they are
  // subject to.
  //
  // Dropping the tab rather than disabling it also decides where that user
  // starts: `useApprovalsTab` falls back to `tabs[0]`, and it rejects a
  // remembered or linked id that names no tab they hold — so the queue is both
  // the default and the only reachable panel.
  const tabs = useMemo(
    () =>
      mayManageRules ? TABS : TABS.filter(tab => tab.id !== APPROVER_RULES_TAB),
    [mayManageRules],
  );

  const [rememberedTab, setRememberedTab] = useApprovalsTab(tabs);

  // The approver's notification mail links to one request, and the monolith
  // redirects that to `/:account_slug/approvals/requests/:externalId` — this
  // page, on the route declared beside `approval_rules`. The param is absent on
  // every other way in.
  //
  // It overrides the remembered tab until the user picks one. A link that names
  // a request is asking for that request, and `useApprovalsTab`'s localStorage
  // read would otherwise open Approver rules over the top of it. The param stays
  // in the URL after arrival, so the override has to end on a tab click or the
  // tabs stop responding.
  const { externalId: deepLinkedRequestId } = useParams<{
    externalId?: string;
  }>();
  const [followingDeepLink, setFollowingDeepLink] = useState(
    Boolean(deepLinkedRequestId),
  );
  const activeTab =
    followingDeepLink && tabs.some(tab => tab.id === APPROVALS_TAB)
      ? APPROVALS_TAB
      : rememberedTab;
  const setActiveTab = useCallback(
    (tab: Parameters<typeof setRememberedTab>[0]) => {
      setFollowingDeepLink(false);
      setRememberedTab(tab);
    },
    [setRememberedTab],
  );

  // Which side of the table is showing, remembered across visits. Every path
  // that moves it goes through this setter, the create menu included: choosing
  // "Opening" there switches the list to Openings so the saved rule lands in
  // view, and that is the view to come back to.
  const [subjectType, setSubjectType] = useSubjectTypeView();
  // Two pieces of state on purpose: `searchInput` keeps the field responsive on
  // every keystroke, `searchTerm` is what actually reaches the server. Driving
  // the request straight from the input would fire one per character.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(
    useCallback((value: string) => setSearchTerm(value), []),
    SEARCH_DEBOUNCE_MS,
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    debouncedSearch(event.target.value);
  };

  // Both pieces of state, and the debounce as well. Setting the two directly is
  // what makes the click feel immediate rather than landing a debounce-length
  // later; calling `debouncedSearch` too is what stops a keystroke made in the
  // last few hundred milliseconds from firing afterwards and searching for a
  // term the field no longer holds. It supersedes that pending call with one
  // that sets the same empty string, so the late fire is a no-op.
  //
  // Reachable from either control that clears the field: the search box's own
  // clear button, which appears as soon as there is text and so is one click
  // away from a still-pending keystroke, and the empty state's Clear search.
  // `debouncedSearch.clear()` would say the third line more directly, but
  // `useDebounce` types its return as a bare callback and erases `.clear`, so
  // that needs a change to the shared hook first.
  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    debouncedSearch('');
  };

  // Which rule the panel is editing. `undefined` rule with the panel open is a
  // create; a rule is an edit. One piece of state rather than two booleans, so
  // "creating and editing at once" is unrepresentable.
  //
  // `level` is the 1-based level card the panel opens on, set when the admin
  // clicked a level row rather than the rule. Part of the same state so it
  // cannot outlive the open it belongs to — a `level` left standing from a
  // previous open would scroll the next rule's panel to an unrelated card.
  const [editing, setEditing] = useState<{
    rule?: ApprovalRule;
    level?: number;
  } | null>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  // The rule awaiting delete confirmation, plus how many in-flight approvals
  // that delete would cancel. `pending: null` means the count is still loading.
  const [deleting, setDeleting] = useState<{
    rule: ApprovalRule;
    pending: number | null;
  } | null>(null);
  // Which row's toggle is mid-flight, so only that switch is held rather than
  // the whole list going inert while one rule saves.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleFailed, setToggleFailed] = useState(false);

  const { result, refetch } = useApprovalRules(
    subjectType,
    searchTerm,
    mayManageRules,
  );

  // Fetched once for the whole page rather than per panel open: the roster does
  // not change while an admin is writing a rule, and re-requesting it on every
  // open would put a spinner in front of the approver field each time.
  // `useApiService` returns { result, refetch, cancel } — the state is one level
  // in, not the return value itself.
  //
  // Authorized like the rules themselves, so held back for the same reason:
  // without the rule-management permission it is a guaranteed 403.
  //
  // Undefined until it resolves, not `[]`: the panel reads a held approver
  // missing from this list as one who cannot approve, and an empty list would
  // say that of everyone.
  const { result: userOptionsResult } = useApiService<{
    approvers: UserOption[];
  }>(
    useCallback(
      () => ApprovalsService.getInternalApiApprovalsApproverOptions(),
      [],
    ),
    { enabled: mayManageRules },
  );
  const userOptions =
    userOptionsResult.status === 'ready'
      ? userOptionsResult.data.approvers
      : undefined;

  // The typed field catalog the condition builder reads (AXHE-3929). Fetched
  // alongside the roster for the same reason: it is account configuration that
  // does not change while a rule is being written.
  const { result: catalogResult, refetch: refetchCatalog } =
    useApiService<CatalogResponse>(
      useCallback(
        () => ApprovalsService.getInternalApiApprovalsFieldCatalog(),
        [],
      ),
      { enabled: mayManageRules },
    );

  // Passed down as a state, not flattened to an array. Collapsing loading and
  // failed to `[]` makes them indistinguishable from an account with no fields
  // to gate on — the builder would render an empty picker with no message and no
  // retry, and on an existing rule its stored conditions would render as blank
  // selects. `useMemo` because this is a prop on a keyed panel; a fresh object
  // per render would defeat the memoisation downstream.
  const catalogState: CatalogState = useMemo(() => {
    if (catalogResult.status === 'ready') {
      const { data } = catalogResult;

      return {
        status: 'ready',
        catalog: {
          fields: data.fields ?? [],
          // Empty only if the server ever ships no unary operators; it is the
          // server's answer either way rather than a copy kept here.
          unaryOperators: data.unary_operators ?? [],
          // The condition-type half (AXHE-4383). Defaulted to empty rather than
          // to a hard-coded vocabulary: an older server that ships none leaves
          // the builder with no types to offer, which is honest, where a
          // client-side fallback would offer types that server cannot store.
          subjects: data.subjects ?? [],
          singleUseTypes: data.single_use_types ?? [],
          comparisonOperators: data.comparison_operators ?? [],
          openingPayRangeEnabled: data.opening_pay_range_enabled ?? false,
          attributeScopes: data.attribute_scopes ?? [],
          sourcedAttributes: (data.sourced_attributes ?? []).map(entry => ({
            scope: entry.scope ?? '',
            attributes: entry.attributes ?? [],
          })),
          options: {
            positions: data.options?.positions ?? [],
            locations: data.options?.locations ?? [],
            openings: data.options?.openings ?? [],
            location_groups: data.options?.location_groups ?? [],
          },
          truncatedOptions: data.truncated_options ?? [],
          emailValuedAttributes: data.email_valued_attributes ?? [],
        },
      };
    }
    return catalogResult.status === 'error'
      ? { status: 'error' }
      : { status: 'loading' };
  }, [catalogResult]);

  // Flattened for the Trigger column, where — unlike the builder — an unloaded
  // catalog is not a state worth telling anyone about. A condition's field falls
  // back to its raw key, which is a worse label and still identifies the field;
  // holding the whole column for a request the row does not otherwise need would
  // trade a legible summary for a blank one.
  const catalogFields =
    catalogState.status === 'ready' ? catalogState.catalog.fields : [];
  // Offers and openings share one attribute list, so either subject's labels it.
  const catalogAttributes = useMemo(
    () =>
      catalogState.status === 'ready'
        ? catalogState.catalog.subjects.flatMap(
            subject => subject.attributes ?? [],
          )
        : [],
    [catalogState],
  );

  /**
   * Open the delete confirmation, then fill in the consequence.
   *
   * The count is fetched when the dialog opens rather than rendered from a
   * hook inside it: the confirmation has to name what the delete destroys
   * BEFORE the admin commits (AXHE-4018), and the dialog stays presentational.
   * The guard on re-entry stops a slow response for one rule from landing in a
   * dialog the admin has since reopened on a different one.
   */
  const requestDelete = async (rule: ApprovalRule) => {
    setDeleting({ rule, pending: null });
    let count = 0;
    try {
      const response =
        await ApprovalsService.getInternalApiApprovalsRulesPendingRequests(
          rule.id as string,
        );
      count = response.pending_requests ?? 0;
    } catch {
      // The cascade warning is still correct without it; failing to count is not
      // a reason to block a delete the admin explicitly asked for.
      count = 0;
    }
    setDeleting(current =>
      current && current.rule.id === rule.id
        ? { ...current, pending: count }
        : current,
    );
  };

  /**
   * Flip a rule active or inactive from its row.
   *
   * Sends only `enabled`. Every other field on a PATCH keeps its current value
   * when omitted — except `levels`, which replaces the whole tree when sent — so
   * the narrow payload is what stops a toggle from rewriting the rule's levels
   * with whatever the list happened to be holding.
   *
   * Not optimistic: this decides whether a compliance gate is running, and a
   * switch that flips locally and silently fails server-side would leave the
   * screen claiming a gate is on when it is off.
   */
  const toggleRule = async (rule: ApprovalRule, enabled: boolean) => {
    setTogglingId(rule.id as string);
    setToggleFailed(false);
    try {
      await ApprovalsService.patchInternalApiApprovalsRules(rule.id as string, {
        rule: {
          name: rule.name as string,
          subject_type: rule.subject_type as SubjectType,
          enabled,
        },
      });
      refetch();
    } catch {
      setToggleFailed(true);
    } finally {
      setTogglingId(null);
    }
  };

  // `reloading` carries the *previous* request's data, and on this page the only
  // thing that triggers a refetch is the filter itself — the toggle or the
  // search term. So the rows it carries always belong to the filter the user
  // just moved away from: switching to Openings would list Offer rules, and a
  // new search would list the unfiltered set, each under a heading claiming
  // otherwise. Showing rows while the request that justifies them is still in
  // flight is presenting stale data as current, so `reloading` is a loading
  // state here, not a data state.
  //
  // Read the flags off the result rather than re-deriving them from `status`:
  // `ReloadingState` already declares `isLoading: true`, and hand-rolling the
  // check is what let the container disagree with its own type in the first
  // place.
  const rules = result.status === 'ready' ? (result.data.rules ?? []) : [];
  const { isLoading, isError } = result;
  const isSearching = searchTerm.trim().length > 0;

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.pageHeading)}</title>
      </Helmet>

      {/* The column wrapper is load-bearing: it is what keeps the nav 52px
          tall. `<Switch>` renders a route's elements straight into the Account
          router's `Grid container`, a flex row that wraps, so two siblings
          become two flex lines. `align-content: stretch` then splits the
          leftover viewport height between those lines, and GlobalNav centers
          its contents inside its share — a band above the nav row, another
          between the row and the row's own bottom border. One child is one
          line, and a column container distributes free space along the cross
          axis, so nothing grows vertically. This is the wrapper every peer
          settings page already has: Openings, CustomerAttributes,
          OfferLetterTemplates, OpeningDetailsSettings. */}
      <Grid container direction="column">
        {/* Now that this route is registered and reachable by direct link
            (AXHE-3945), the page needs the recruiter chrome its settings-route
            peers render — OfferLetterTemplates, CustomerAttributes,
            TranslationSettings and UserGroupsManagement all render GlobalNav
            themselves. The Account router only supplies GlobalDrawer, which is
            the mobile nav, so without this a desktop user following a direct
            link had no in-app way out at all: no nav, no breadcrumb, no back.
            GlobalNav handles its own breakpoints internally, so it is rendered
            bare rather than wrapped in Hidden. */}
        <GlobalNav />

        <div className={styles.page}>
          {/* The way out, above the title, as every other settings page has it.
              This page is reached from the App Settings sidebar in the monolith,
              which is a different origin — so the browser's own Back is the only
              other route out, and a direct link leaves even that empty. */}
          <BackToCompanySettings
            label={messages.backToSettings}
            authorized={mayViewApprovals}
          />

          <Typography variant="h2">
            <FormattedMessage {...messages.pageHeading} />
          </Typography>
          {/* `body2` carries the secondary grey in the variant itself, so no
              colour prop on top of it. Note the FDS scale is not the MUI one —
              `subtitle1` is 24px here, the same size as the `h2` above. */}
          <Typography className={styles.subtitle} variant="body2">
            <FormattedMessage {...messages.pageSubtitle} />
          </Typography>

          <ApprovalsTabs
            ariaLabel={intl.formatMessage(messages.pageHeading)}
            onChange={setActiveTab}
            tabs={tabs}
            value={activeTab}
          />

          {/* Each tab's content is its own labelled panel, and only the open one
              renders. `hidden` on both would keep the rule table's DOM — and its
              horizontal scroll region — reachable by keyboard from behind the
              queue. */}
          {activeTab === APPROVER_RULES_TAB && (
            <div
              aria-labelledby={tabId(APPROVER_RULES_TAB)}
              id={tabPanelId(APPROVER_RULES_TAB)}
              role="tabpanel"
            >
              <div className={styles.toolbar}>
                {/* A split button rather than a plain one: a rule's subject type is
                  fixed at creation and cannot be changed afterwards, so choosing it is
                  part of starting, not part of editing.

                  `outlined`, as the design has it. Creating a rule is not this
                  page's primary act — reading the rules that are already running
                  is — and a filled button here competes with the row controls that
                  turn a live compliance gate on and off.

                  `aria-haspopup` plus `aria-expanded` tell a screen reader this opens a
                  menu rather than submitting — without them the caret is the only
                  cue, and it is decorative. */}
                <Button
                  aria-controls={createMenuAnchor ? CREATE_MENU_ID : undefined}
                  aria-expanded={Boolean(createMenuAnchor)}
                  aria-haspopup="menu"
                  color="primary"
                  endIcon={
                    <Icon
                      icon={ChevronDownOutlineIcon}
                      size="sm"
                      color="inherit"
                    />
                  }
                  id={CREATE_BUTTON_ID}
                  variant="outlined"
                  onClick={event => setCreateMenuAnchor(event.currentTarget)}
                >
                  <FormattedMessage {...messages.createRule} />
                </Button>
                {/* Both ids go through `MenuListProps`, which is the only way they
                  reach the element that actually carries `role="menu"`. `Menu` does
                  not destructure `id` — it falls through to `Popover` and then
                  `Modal`, which put it on the outer `role="presentation"` wrapper —
                  so `id` passed directly would leave the button's `aria-controls`
                  pointing at a presentational div instead of the menu. */}
                <Menu
                  anchorEl={createMenuAnchor}
                  MenuListProps={{
                    'aria-labelledby': CREATE_BUTTON_ID,
                    id: CREATE_MENU_ID,
                  }}
                  open={Boolean(createMenuAnchor)}
                  onClose={() => setCreateMenuAnchor(null)}
                >
                  {/* The icons carry no information the label does not already give,
                    so they are hidden from assistive tech rather than labelled
                    twice. `ListItemText` rather than a bare child so the label
                    takes the list's own typography instead of the menu item's. */}
                  <MenuItem
                    onClick={() => {
                      setCreateMenuAnchor(null);
                      setSubjectType(OFFER);
                      setEditing({});
                    }}
                  >
                    <ListItemIcon
                      aria-hidden="true"
                      className={styles.menuItemIcon}
                    >
                      <Icon
                        icon={DocumentTextOutlineIcon}
                        size="sm"
                        color="inherit"
                      />
                    </ListItemIcon>
                    <ListItemText>
                      <FormattedMessage {...messages.createOfferRule} />
                    </ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setCreateMenuAnchor(null);
                      setSubjectType(OPENING);
                      setEditing({});
                    }}
                  >
                    <ListItemIcon
                      aria-hidden="true"
                      className={styles.menuItemIcon}
                    >
                      <Icon
                        icon={BriefcaseOutlineIcon}
                        size="sm"
                        color="inherit"
                      />
                    </ListItemIcon>
                    <ListItemText>
                      <FormattedMessage {...messages.createOpeningRule} />
                    </ListItemText>
                  </MenuItem>
                </Menu>

                {/* Both right-hand controls narrow the same list, so they sit
                  together at the end of the row (Design Requirements §2): two
                  sides of one rule table, filtered by subject_type, and a name
                  search over whichever side is showing. */}
                <div className={styles.toolbarFilters}>
                  <SubjectTypeToggle
                    value={subjectType}
                    onChange={setSubjectType}
                  />
                  {/* The shared search field every other recruiter-ui list toolbar
                    uses, rather than a labelled `TextField`: the magnifier is one
                    of the glyphs `constraint-anti-pattern-guard` names as needing
                    no text label, and a floating label would leave this the only
                    48px control in a row of 32px ones. `inputProps` is what keeps
                    the label as the field's accessible name, so the placeholder
                    repeats a name rather than standing in for one. */}
                  <SearchInput
                    /* @ts-expect-error this will go away when `SearchInput` is converted to typescript */
                    clearLabel={intl.formatMessage(messages.clearSearchField)}
                    inputProps={{
                      'aria-label': intl.formatMessage(messages.searchLabel),
                    }}
                    placeholder={intl.formatMessage(messages.searchLabel)}
                    value={searchInput}
                    onChange={handleSearchChange}
                    onClear={clearSearch}
                  />
                </div>
              </div>

              {/* Above the list, not inside it: an error is about the request, not about
                a row, and burying it in the table body reads as "no rules" — which is
                the one thing it does not mean. */}
              {isError && (
                <div className={styles.error} role="alert">
                  <Typography variant="body1">
                    <FormattedMessage {...messages.loadError} />
                  </Typography>
                  <Button variant="outlined" onClick={refetch}>
                    <FormattedMessage {...messages.retry} />
                  </Button>
                </div>
              )}

              {/* A failed toggle is reported here rather than on the row: the switch has
                snapped back to where it was, and without a message that reads as the
                click not registering rather than the save being refused. */}
              {toggleFailed && (
                <div className={styles.error} role="alert">
                  <Typography variant="body1">
                    <FormattedMessage {...messages.toggleFailed} />
                  </Typography>
                </div>
              )}

              {/* One table for the loading, empty and populated states, rather than a
                table for rows and a bare div for the rest. The column headers are
                what say what the rows will hold, so dropping them while a request is
                in flight makes the region change shape twice per filter change — and
                the empty and loading states then have no width to centre in.
                `layout-composition` puts both inside the table body for that reason.

                A *failed* request is the exception: `isError` suppresses the
                empty-state row and `rules` is empty, so the container came out as
                headers over a zero-row body — a bordered empty box under a banner
                that has already explained itself, which reads as a second fault
                rather than the same one. The banner carries Try again, so there is
                nothing left for the region to hold.

                `role="region"` with a `tabIndex` because this container scrolls: the
                Trigger column holds account-authored text, so `overflowX: auto` on
                `styles.list` genuinely engages on a narrow window. Without a focus
                stop a keyboard-only user could pan it only as a side effect of
                tabbing to the rightmost buttons, and in the loading and empty states
                there is nothing focusable inside at all — WCAG 2.1.1, axe's
                `scrollable-region-focusable`. jsdom has no layout, so no test here
                can see it.

                The region and the table take the same name deliberately: they are
                announced in different modes — the region on tabbing into the
                scrollable area, the table on navigating cell by cell — and an
                unnamed `<table>` is its own gap. */}
              {!isError && (
                <div
                  aria-label={intl.formatMessage(messages.pageTitle)}
                  className={styles.list}
                  role="region"
                  tabIndex={0}
                >
                  <Table
                    aria-label={intl.formatMessage(messages.pageTitle)}
                    className={styles.table}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.headerCell}>
                          <FormattedMessage {...messages.columnRule} />
                        </TableCell>
                        <TableCell className={styles.headerCell}>
                          <FormattedMessage {...messages.columnType} />
                        </TableCell>
                        <TableCell
                          className={classNames(
                            styles.headerCell,
                            styles.triggerCell,
                          )}
                        >
                          <FormattedMessage {...messages.columnTrigger} />
                        </TableCell>
                        <TableCell className={styles.headerCell}>
                          <FormattedMessage {...messages.columnApprovers} />
                        </TableCell>
                        <TableCell className={styles.headerCell}>
                          <FormattedMessage {...messages.columnEnabled} />
                        </TableCell>
                        {/* Named for a screen reader reading the table column by
                          column, and unnamed on screen: the two icon buttons under it
                          are self-describing, and the design has no header there. An
                          empty `<th>` is the version that reads as a gap. */}
                        <TableCell
                          className={classNames(
                            styles.headerCell,
                            styles.actionsCell,
                          )}
                        >
                          <span className={styles.visuallyHidden}>
                            <FormattedMessage {...messages.columnActions} />
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {isLoading && (
                        <TableRow>
                          <TableCell
                            className={styles.stateCell}
                            colSpan={COLUMN_COUNT}
                          >
                            <CircularProgress
                              size={20}
                              aria-label={intl.formatMessage(
                                messages.loadingRules,
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      )}

                      {!isLoading && !isError && rules.length === 0 && (
                        <TableRow>
                          <TableCell
                            className={styles.empty}
                            colSpan={COLUMN_COUNT}
                          >
                            {/* A search that found nothing is not the same state as an
                              account with no rules: one is recoverable by clearing the
                              search, the other needs a rule created. Same blank area,
                              different message and different way out. Kept as two
                              states against Design Requirements §2, which has one for
                              both — see `emptyTitle` in `messages.ts` for why
                              (AXHE-4468). */}
                            {/* `h3` for size — this theme's `h6` is 12px, which would
                              leave the title smaller than the sentence under it — and
                              `h3` is now also the right *element*, so no `component`
                              override. #4172 needed one because the page title above
                              was an `h4` at the time; this change moved it to `h2`, so
                              the variant's own `<h3>` is exactly one level below the
                              title. */}
                            <Typography variant="h3">
                              {isSearching ? (
                                <FormattedMessage
                                  {...messages.noSearchResultsTitle}
                                  values={{ term: searchTerm.trim() }}
                                />
                              ) : (
                                <FormattedMessage {...messages.emptyTitle} />
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              className={styles.emptyBody}
                            >
                              {isSearching ? (
                                <FormattedMessage
                                  {...messages.noSearchResultsBody}
                                />
                              ) : (
                                <FormattedMessage {...messages.emptyBody} />
                              )}
                            </Typography>
                            {/* The sentence above already names clearing the search as
                              the way out; this is that sentence made clickable, which
                              is what `layout-composition` asks a filtered-empty state
                              for — "an empty state with an offer to clear filters",
                              not a description of one. No counterpart on the no-rules
                              state: "Create rule" is a few pixels up in the header,
                              and a second one here would be two primary actions for
                              the same job. */}
                            {isSearching && (
                              <Button
                                variant="outlined"
                                className={styles.emptyAction}
                                onClick={clearSearch}
                              >
                                <FormattedMessage {...messages.clearSearch} />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )}

                      {!isLoading &&
                        rules.map(rule => (
                          <RuleRow
                            key={rule.id}
                            catalogFields={catalogFields}
                            catalogAttributes={catalogAttributes}
                            rule={rule}
                            toggling={togglingId === rule.id}
                            onEdit={level => setEditing({ rule, level })}
                            onToggle={enabled => toggleRule(rule, enabled)}
                            onDelete={() => requestDelete(rule)}
                          />
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {activeTab === APPROVALS_TAB && (
            <div
              aria-labelledby={tabId(APPROVALS_TAB)}
              id={tabPanelId(APPROVALS_TAB)}
              role="tabpanel"
            >
              {/* Only while the deep link is being followed: the queue unmounts
                  with its tab, and a prop that outlived the tab click would
                  reopen the panel every time the approver came back. */}
              <ApprovalsQueue
                initialRequestId={
                  followingDeepLink ? deepLinkedRequestId : undefined
                }
              />
            </div>
          )}

          {/* Keyed so switching between rules remounts the panel: the form holds the
              draft in its own state, and a reused instance would keep the previously
              opened rule's values while displaying the new rule's title. */}
          {deleting && (
            <DeleteRuleDialog
              rule={deleting.rule}
              pendingRequests={deleting.pending ?? 0}
              pendingLoading={deleting.pending === null}
              onCancel={() => setDeleting(null)}
              onDeleted={() => {
                // Same re-entry guard as the pending-count fetch above. The dialog
                // discards the canceller `mutation()` returns, so dismissing it does
                // not abort the DELETE already in flight: that request still
                // resolves and still calls back. Closing unconditionally would then
                // shut a confirmation the admin has since opened on a *different*
                // rule, with no explanation and that second rule untouched.
                //
                // `deletedRule` is this render's rule, and the in-flight promise
                // captured this render's callback — so it names the rule whose
                // deletion actually completed, not whatever is on screen now.
                const deletedRule = deleting.rule;

                setDeleting(current =>
                  current && current.rule.id === deletedRule.id
                    ? null
                    : current,
                );
                refetch();
              }}
            />
          )}

          {editing && (
            <RuleEditorPanel
              key={editing.rule?.id ?? 'new'}
              open
              focusLevel={editing.level}
              rule={editing.rule}
              subjectType={subjectType}
              userOptions={userOptions}
              dynamicApproversEnabled={dynamicApproversEnabled}
              catalog={catalogState}
              onRetryCatalog={refetchCatalog}
              onClose={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                refetch();
              }}
            />
          )}
        </div>
      </Grid>
    </>
  );
};

export default ApprovalRules;
